import { useDialogRoute } from "@dash/dialog";
import { Button, TextField } from "@dash/form";
import { fetchPlace, SearchNearbyOptions, useAutocomplete, useNearbySearch } from "@dash/google-map";
import { useDeviceType } from "@dash/mq";
import { SectionHeader } from "@dash/page";
import { SelectDialog } from "@dash/select";
import { TileCard } from "@dash/tile";
import { useCallback } from "react";
import { MdOutlineEdit, MdOutlineLocationOn } from "react-icons/md";

import { SameGooglePlaceAlert } from "./SameGooglePlaceAlert";
import { CustomerData, ExtendedCustomerAddress } from "./useCustomerDetailsForm";

type Props = {
  customer: CustomerData;
  onAddressChange: (address: ExtendedCustomerAddress) => void;
};

export const PublicProfile: React.FC<Props> = ({ customer, onAddressChange }) => {
  const { isDesktop } = useDeviceType();

  const { searchPredictions } = useAutocomplete();
  const { places, searchNearby } = useNearbySearch();

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<ExtendedCustomerAddress | undefined> => {
      const place = places.find((p) => p.id === placeId);

      if (!place) {
        const details = await fetchPlace(placeId);

        if (!details) {
          return undefined;
        }

        const { street, postalCode, city, country, latitude, longitude, ...rest } = details;
        return { ...customer.address, ...rest };
      }

      return {
        ...customer.address,
        googlePlaceId: place.id,
        listedName: place.displayName.text,
        listedPhone: place.nationalPhoneNumber,
        listedWebsite: place.websiteUri,
      };
    },
    [customer.address, places],
  );

  const dialog = useDialogRoute(`/edit-google-place`, ({ onClose }) => (
    <SelectDialog
      label="Wybierz miejsce w Google Maps"
      search={async (searchText) => {
        if (searchText) {
          const predictions = await searchPredictions(searchText, { includedTypes: ["establishment"] });
          return predictions?.map((prediction) => ({ label: prediction.description, value: prediction.id })) ?? [];
        }

        return places.map((place) => ({
          label: place.displayName.text,
          value: place.id,
        }));
      }}
      initiallySelectedValues={customer.address.googlePlaceId ? [customer.address.googlePlaceId] : []}
      variant="single-select"
      onSelect={async (option) => {
        if (option?.value === customer.address.googlePlaceId || !option) {
          onAddressChange({
            ...customer.address,
            googlePlaceId: null,
            listedName: null,
            listedPhone: null,
            listedWebsite: null,
          });
          return;
        }
        const placeDetails = await getPlaceDetails(option.value);

        onAddressChange({
          ...customer.address,
          googlePlaceId: option?.value ?? null,
          listedName: placeDetails?.listedName ?? null,
          listedPhone: placeDetails?.listedPhone ?? null,
          listedWebsite: placeDetails?.listedWebsite ?? null,
        });
      }}
      isOpen
      onClose={onClose}
      noTrigger
    />
  ));

  const handleGoogleChange = (field: keyof ExtendedCustomerAddress) => (v: string | null) => {
    onAddressChange({
      ...customer.address,
      [field]: v,
    });
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%", flexGrow: 0 }}>
      <SectionHeader isHr>Profil publiczny</SectionHeader>
      {customer.address.googlePlaceId && <SameGooglePlaceAlert googlePlaceId={customer.address.googlePlaceId} customerUuid={customer.uuid} />}
      <TileCard
        label="Miejsce w Google Maps"
        value={customer.address.googlePlaceId ? customer.address.listedName ?? "Brak nazwy" : "Brak"}
        isActive={!!customer.address.googlePlaceId}
        Icon={MdOutlineLocationOn}
        actions={
          <Button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              searchNearby(
                { latitude: customer.address.latitude, longitude: customer.address.longitude },
                searchNearbyOptions,
              );
              dialog.open();
            }}
            icon={<MdOutlineEdit size={16} />}
            variant="outline"
            css={{ border: "none", padding: "0.5rem" }}
          />
        }
      />

      <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div
          css={{ display: "flex", gap: isDesktop ? "0.625rem" : "1rem", flexDirection: isDesktop ? "row" : "column" }}
        >
          <TextField
            label="Nazwa"
            value={customer.address.listedName ?? ""}
            onChange={handleGoogleChange("listedName")}
          />
          <TextField
            label="Numer telefonu"
            value={customer.address.listedPhone ?? ""}
            onChange={handleGoogleChange("listedPhone")}
          />
        </div>
        <TextField
          label="Adres strony internetowej"
          value={customer.address.listedWebsite ?? ""}
          onChange={handleGoogleChange("listedWebsite")}
        />
      </div>

      {dialog.element}
    </div>
  );
};

const searchNearbyOptions: SearchNearbyOptions = {
  includedTypes: ["store"],
  maxResultCount: 10,
  rankPreference: "DISTANCE",
};
